/**
 * Helpers for Correct Score / Half Time Correct Score picker.
 * Maps (homeScore, awayScore) to value key "0-0", "1-0", "4-0", etc. (numeric only; no "4+").
 */

import type { MarketOddValue } from '../../../src/create/types/create.types';

/** @deprecated No longer used; bounds come from getCorrectScoreBounds(). Kept for backward compat with stale bundles. */
export const CORRECT_SCORE_UI_MAX = 99;

/** Score segment in picker; any non-negative integer. */
export type ScorePickerValue = number;

/** Normalize API key so "4+-0" / "4-0" both map to "4-0" for lookup. */
function normalizeScoreKeyForLookup(key: string): string {
  const parts = key.split('-').map((p) => p.trim());
  if (parts.length !== 2) return key;
  const h = parts[0] === '4+' ? '4' : parts[0];
  const a = parts[1] === '4+' ? '4' : parts[1];
  return `${h}-${a}`;
}

export function getScoreKey(home: number, away: number): string {
  return `${home}-${away}`;
}

export function getOddForScore(
  home: number,
  away: number,
  values: MarketOddValue[]
): number | null {
  const key = getScoreKey(home, away);
  const found = values.find((v) => normalizeScoreKeyForLookup((v.value ?? '').trim()) === key);
  return found != null ? Number(found.odd) : null;
}

/** Lookup map: score key -> odd. Use for O(1) neighbor checks and deltas. Normalizes "4+" to "4" so API keys match getScoreKey. */
export function buildScoreLookup(values: MarketOddValue[]): Map<string, number> {
  const map = new Map<string, number>();
  for (const v of values ?? []) {
    const raw = (v.value ?? '').trim();
    if (!raw) continue;
    const key = normalizeScoreKeyForLookup(raw);
    map.set(key, Number(v.odd) || 0);
  }
  return map;
}

export type NeighborDirection = 'homeUp' | 'homeDown' | 'awayUp' | 'awayDown';

/** Neighbor info for one +/- button: exists in lookup, and delta (nextOdd - currentOdd) when exists. */
export function getNeighborInfo(
  direction: NeighborDirection,
  home: number,
  away: number,
  lookup: Map<string, number>
): { exists: boolean; delta: number | null } {
  const currentKey = getScoreKey(home, away);
  const currentOdd = lookup.get(currentKey) ?? null;
  let neighborKey: string;
  if (direction === 'homeUp') neighborKey = getScoreKey(home + 1, away);
  else if (direction === 'homeDown') neighborKey = home >= 1 ? getScoreKey(home - 1, away) : '';
  else if (direction === 'awayUp') neighborKey = getScoreKey(home, away + 1);
  else neighborKey = away >= 1 ? getScoreKey(home, away - 1) : '';
  const exists = !!neighborKey && lookup.has(neighborKey);
  if (!exists || currentOdd == null) return { exists, delta: null };
  const nextOdd = lookup.get(neighborKey)!;
  const delta = Math.round((nextOdd - currentOdd) * 100) / 100;
  return { exists, delta };
}

/** Parse one part of a score key ("0"-"9", "4+", etc.) to numeric. */
function parseScorePart(part: string): number {
  const s = String(part).trim();
  if (s === '4+') return 4;
  const n = parseInt(s, 10);
  return Number.isNaN(n) ? 0 : Math.max(0, n);
}

/** Parse score key "0-0", "2-1", "4+-0" to { home, away }. */
export function parseScoreKey(key: string): { home: number; away: number } | null {
  const s = String(key ?? '').trim();
  if (!s) return null;
  const parts = s.split('-');
  if (parts.length !== 2) return null;
  const home = parseScorePart(parts[0]);
  const away = parseScorePart(parts[1]);
  return { home, away };
}

/**
 * Bounds of scores we have odds for (from market values keys like "0-0", "1-0", "5-0").
 * No cap – uses whatever the API provides.
 */
export function getCorrectScoreBounds(
  values: MarketOddValue[]
): { maxHome: number; maxAway: number } {
  let maxHome = 0;
  let maxAway = 0;
  for (const v of values) {
    const key = (v.value ?? '').trim();
    if (!key) continue;
    const parts = key.split('-');
    if (parts.length !== 2) continue;
    const h = parseScorePart(parts[0]);
    const a = parseScorePart(parts[1]);
    maxHome = Math.max(maxHome, h);
    maxAway = Math.max(maxAway, a);
  }
  return { maxHome, maxAway };
}

/** Change when increasing score: currentOdd − nextOdd (e.g. 0-0=5 → 1-0=4 gives 5−4=1). */
export function getDeltaUp(
  isHome: boolean,
  home: number,
  away: number,
  values: MarketOddValue[]
): number | null {
  const current = getOddForScore(home, away, values);
  if (current == null) return null;
  const newHome = isHome ? home + 1 : home;
  const newAway = !isHome ? away + 1 : away;
  if (newHome === home && newAway === away) return null;
  const next = getOddForScore(newHome, newAway, values);
  if (next == null) return null;
  return Math.round((current - next) * 100) / 100;
}

/** Change when decreasing score: currentOdd − nextOdd (e.g. 1-0=4 → 0-0=5 gives 4−5=−1). */
export function getDeltaDown(
  isHome: boolean,
  home: number,
  away: number,
  values: MarketOddValue[]
): number | null {
  const current = getOddForScore(home, away, values);
  if (current == null) return null;
  const newHome = isHome ? Math.max(home - 1, 0) : home;
  const newAway = !isHome ? Math.max(away - 1, 0) : away;
  if (newHome === home && newAway === away) return null;
  const next = getOddForScore(newHome, newAway, values);
  if (next == null) return null;
  return Math.round((current - next) * 100) / 100;
}

/** Display score in picker (always the number, no "4+" label). */
export function formatScoreDisplay(value: ScorePickerValue): string {
  return String(value);
}

/** Format odd for display; 100+ rounded to 1 decimal to avoid wrapping (e.g. 325.00 → "325.0"). */
export function formatOddDisplay(odd: number): string {
  if (odd >= 100) return Number(odd).toFixed(1);
  return odd.toFixed(2);
}

/** Format delta for display: "1", "3.5". Use with deltaDisplay: green = odd goes down, red = odd goes up. */
export function formatDeltaMagnitude(delta: number): string {
  const rounded = Math.round(Math.abs(delta) * 100) / 100;
  return String(rounded);
}

/** Label with '-' prefix for red values (delta >= 0). */
export function formatDeltaLabel(delta: number): string {
  const label = formatDeltaMagnitude(delta);
  return delta >= 0 ? `-${label}` : label;
}

/** Delta with sign; 100+ uses 1 decimal to avoid wrap (e.g. "+325.0", "-325.0"), otherwise .toFixed(2). */
export function formatDeltaLabelFixed(delta: number): string {
  const sign = delta >= 0 ? '+' : '-';
  const abs = Math.abs(delta);
  const mag = abs >= 100 ? Number(abs).toFixed(1) : abs.toFixed(2);
  return sign + mag;
}

/** Visual priority: green = odd goes up (higher payout), gray/red = odd goes down. */
export function getDeltaStyleKeyVisual(
  delta: number | null,
  isDisabled: boolean
): 'deltaPositive' | 'deltaNegative' | 'deltaHidden' {
  if (isDisabled) return 'deltaHidden';
  if (delta == null) return 'deltaHidden';
  return delta > 0 ? 'deltaPositive' : 'deltaNegative';
}
